import React from "react";

export interface PricingWindowOption {
  key: string;
  label: string;
  dateRange: string;
  isActive: boolean;
}

interface Props {
  options: PricingWindowOption[];
  compact?: boolean;
}

/**
 * Shows Early Bird / Standard / On-Site pricing periods on a connected timeline.
 * The active window is determined automatically by the registration date.
 */
export default function PricingWindowPanel({ options, compact = false }: Props) {
  return (
    <div className={compact ? "mb-6" : "mb-8"}>
      <p className="text-sm font-medium text-slate-700 mb-2">
        Full Conference pricing period
      </p>
      <p className="text-xs text-slate-500 mb-4">
        Your rate is set automatically from the date you register. The active
        period is highlighted below.
      </p>

      <div className="flex items-start">
        {options.map((opt, idx) => (
          <React.Fragment key={opt.key}>
            <div
              className={`flex-1 min-w-0 text-center px-1 sm:px-2 ${
                opt.isActive ? "" : "opacity-80"
              }`}
              aria-current={opt.isActive ? "true" : undefined}
            >
              <div
                className={`mx-auto w-3 h-3 rounded-full border-2 ${
                  opt.isActive
                    ? "border-amber-500 bg-amber-500"
                    : "border-slate-300 bg-white"
                }`}
              />
              <p
                className={`text-xs sm:text-sm font-semibold mt-2 leading-tight ${
                  opt.isActive ? "text-amber-900" : "text-slate-700"
                }`}
              >
                {opt.label}
                {opt.isActive && (
                  <span className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-amber-700 mt-0.5">
                    Your rate
                  </span>
                )}
              </p>
              <p
                className={`text-[10px] sm:text-xs mt-1 leading-snug ${
                  opt.isActive ? "text-amber-800" : "text-slate-500"
                }`}
              >
                {opt.dateRange}
              </p>
            </div>
            {idx < options.length - 1 && (
              <div
                className="flex-shrink-0 w-6 sm:w-10 h-px bg-slate-300 mt-1.5"
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="border-b border-slate-200 mt-5" aria-hidden="true" />
    </div>
  );
}
